/* @flow */
import moment from 'moment';
import type { TFunction } from 'react-i18next';

import navigation from 'services/NavigationService';
import type { DataSource, CustomerAccount } from 'models/CustomerAccounts';
import { apiService } from 'services/ServiceRegistry';
import { activities } from 'components/Activity/ActivityData';

import Form from '../_.extend';

const getDataSourceOptions = (t: TFunction) => [
  {
    id: 'er',
    name: t('general:customer_accounts.data_sources.er.title'),
  },
  {
    id: 'pf',
    name: t('general:customer_accounts.data_sources.pf.title'),
  },
];

const getDataSourceOption = (dataSource: DataSource, t: TFunction) => {
  const option = getDataSourceOptions(t).find(item => item.id === dataSource);

  if (!option) {
    return {
      id: dataSource,
    };
  }

  return option;
};

export const makeFields = (
  t: TFunction,
  mode: string,
  dataSource?: DataSource,
  customerAccount?: CustomerAccount
) => {
  return [
    {
      name: 'details',
      label: t('general:customer_accounts.labels.details'),
      fields: [
        {
          name: 'id',
          label: t('general:customer_accounts.labels.id'),
          rules: 'required|string',
          value: mode === 'edit' && customerAccount ? customerAccount.customerId : null,
        },
        {
          name: 'dataSource',
          label: t('general:customer_accounts.labels.data_source'),
          rules: 'required',
          value: mode === 'edit' && dataSource ? getDataSourceOption(dataSource, t) : null,
          options: getDataSourceOptions(t),
        },
        {
          name: 'name',
          label: t('general:customer_accounts.labels.name'),
          rules: 'required|string',
          value: mode === 'edit' && customerAccount ? customerAccount.customerName : null,
        },
      ],
    },
    {
      name: 'erCompanies',
      label: t('general:customer_accounts.labels.er_companies'),
      fields: [
        {
          name: 'companyCode',
          label: t('general:customer_accounts.labels.company_code'),
          rules: 'required',
          value: mode === 'edit' && customerAccount ? customerAccount.companyCode : null,
        },
      ],
    },
  ];
};

class CustomerAccountFormCreate extends Form {}

class CustomerAccountFormEdit extends Form {}

export const customerAccountCreate = (fields: Array<*>, localState: Object, t: Function) => {
  const hooks = {
    onSuccess: async function(form) {
      localState.firstLoad = false;
      localState.isLoading = true;
      localState.responseMessage = t('general:messages.saving_form');
      localState.showResponseMessage = true;
      localState.hasError = false;
      localState.hasSuccess = true;

      const jsonObj: { customerName: string, companyCode: string, customerId: string } = {
        customerId: form.$('details.id').value,
        customerName: form.$('details.name').value,
        companyCode: form.$('erCompanies.companyCode').value,
      };

      apiService
        .createResource(
          `customer-account-management/${form.$('details.dataSource').value.id}`,
          jsonObj
        )
        .then(response => {
          localState.isLoading = false;
          localState.responseMessage = t('general:messages.save_success');

          setTimeout(() => {
            localState.showResponseMessage = false;
            setTimeout(() => {
              navigation.goBack();
            }, 225);
          }, 1200);

          return response;
        })
        .catch(err => {
          const error =
            typeof err.data === 'object' && err.data !== null && Object.values(err.data)[0];

          localState.isLoading = false;
          localState.responseMessage = error || t('general:messages.save_error');
          localState.showResponseMessage = true;
          localState.hasSuccess = false;
          localState.hasError = true;
        });
    },
    onError(form) {
      localState.isLoading = false;
      localState.responseMessage = t('general:messages.form_error');
      localState.showResponseMessage = true;
      localState.hasSuccess = false;
      localState.hasError = true;
    },
  };

  return new CustomerAccountFormCreate(
    { fields },
    { hooks, name: t('general:actions.create_new_customer_account') }
  );
};

export const customerAccountEdit = (
  fields: Array<*>,
  customerAccount: CustomerAccount | {},
  localState: Object,
  t: Function
) => {
  localState.selectedErCompany = customerAccount.code;

  const hooks = {
    async onSuccess(form) {
      localState.firstLoad = false;
      localState.isLoading = true;
      localState.responseMessage = t('general:messages.saving_form');
      localState.showResponseMessage = true;
      localState.hasError = false;
      localState.hasSuccess = true;

      const jsonObj: { customerName: string, companyCode: string, customerId: string } = {
        customerId: form.$('details.id').value,
        customerName: form.$('details.name').value,
        companyCode: form.$('erCompanies.companyCode').value,
      };

      apiService
        .putResource(
          `customer-account-management/${form.$('details.dataSource').value.id}/${
            customerAccount.customerId
          }/${customerAccount.companyCode}`,
          jsonObj
        )
        .then(response => {
          localState.isLoading = false;
          localState.responseMessage = t('general:messages.save_success');

          setTimeout(() => {
            localState.showResponseMessage = false;
            /*eslint-disable max-nested-callbacks*/
            setTimeout(() => {
              // Snackbar animation 225ms
              navigation.goBack();
            }, 225);
          }, 1200);

          return response;
        })
        .catch(err => {
          localState.isLoading = false;
          localState.responseMessage = t('general:messages.save_error');
          localState.hasSuccess = false;
          localState.hasError = true;
        });
    },
    onError(form: Object) {
      localState.isLoading = false;
      localState.responseMessage = t('general:messages.form_error');
      localState.showResponseMessage = true;
      localState.hasSuccess = false;
      localState.hasError = true;
    },
  };

  return new CustomerAccountFormEdit(
    { fields },
    { hooks, name: t('general:actions.edit_customer_account') }
  );
};
